import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/app/store';
import { fetchPastWebinarsData } from '../store/analyticsSlice';
import { WebinarStatsTable } from './WebinarStatsTable';
import { Pagination } from '@/components/ui/pagination';

const PAGE_SIZE = 5;

export const PastWebinarsTab = () => {
  const { t } = useTranslation('analytics');
  const dispatch = useDispatch<AppDispatch>();
  const [pageNumber, setPageNumber] = useState(0);

  const activeWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const activeWorkspaceId = activeWorkspace?.id;
  const {
    list: webinarStatisticsList,
    loading: isStatisticsLoading,
    totalPages: totalPagesFromApi,
  } = useSelector((state: RootState) => state.analytics.pastWebinars);

  // Remettre la page à zéro pendant le rendu et non dans un effet : sinon le changement d'espace de
  // travail déclencherait d'abord une requête sur la page précédemment consultée.
  const [loadedWorkspaceId, setLoadedWorkspaceId] = useState(activeWorkspaceId);
  if (activeWorkspaceId !== loadedWorkspaceId) {
    setLoadedWorkspaceId(activeWorkspaceId);
    setPageNumber(0);
  }

  // Le serveur recalcule présence et temps de visionnage pour chaque session renvoyée : ne demander
  // que la page affichée.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    dispatch(
      fetchPastWebinarsData({
        workspaceId: activeWorkspaceId,
        page: pageNumber,
        size: PAGE_SIZE,
      }),
    );
  }, [dispatch, activeWorkspaceId, pageNumber]);

  const totalPages = Math.max(1, totalPagesFromApi);

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    setPageNumber((p) => (p > maxPage ? maxPage : p));
  }, [totalPages]);

  const hasNoData = !isStatisticsLoading && webinarStatisticsList.length === 0;

  return (
    <div className="flex flex-col gap-6 px-2 pb-10">
      <h2 className="text-xl font-semibold text-dark">{t('pastWebinars.title')}</h2>

      <div className="w-full min-h-[400px]">
        {isStatisticsLoading && webinarStatisticsList.length === 0 ? (
          <div className="flex items-center justify-center py-32 bg-white border border-medium rounded-[20px]">
            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin mr-3" />
            <p className="text-role-tertiary font-medium">{t('pastWebinars.loading')}</p>
          </div>
        ) : hasNoData ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white border border-dashed border-medium rounded-[20px]">
            <p className="text-role-tertiary text-lg font-medium">
              {t('pastWebinars.empty.title')}
            </p>
            <p className="text-role-tertiary/60 text-sm mt-1">{t('pastWebinars.empty.sub')}</p>
          </div>
        ) : (
          <>
            <WebinarStatsTable
              data={webinarStatisticsList}
              fallbackTimezone={activeWorkspace?.timezone}
            />
            <div className="mt-4">
              <Pagination
                currentPage={pageNumber}
                totalPages={totalPages}
                onPageChange={setPageNumber}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
